import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTestByIdforall } from '../../actions/testActions';
import {
    Card,
    Button,
    Badge,
    Alert,
    ListGroup,
    ProgressBar,
    Spinner,
    Toast,
    Dropdown,
    ButtonGroup,
    InputGroup,
    Form,
    Container,
    Row,
    Col
} from 'react-bootstrap';
import {
    FaClock,
    FaShareAlt,
    FaUserTie,
    FaBook,
    FaChartBar,
    FaWhatsapp,
    FaTelegram,
    FaEnvelope,
    FaArrowLeft,
    FaLink,
    FaExclamationTriangle,
    FaCalendarAlt
} from 'react-icons/fa';
import {
    MdOutlineContentCopy,
    MdOutlineCheck
} from 'react-icons/md';
// import './TestDetailsPage.css';
import { slug as slugify } from "github-slugger";
import SEO from '../common/SEO';

function TestDetailsPage() {
    const { testId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { testbyidforall: test, fetchingtestbyidforall: loading } = useSelector(state => state.tests);
    const isAuthenticated = Boolean(localStorage.getItem("userId"));
    // State for UI feedback
    const [showCopiedToast, setShowCopiedToast] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const studentProfileData = useSelector(state => state.dashboard.userData);

    useEffect(() => {
        if (!test || test._id !== testId) {
            dispatch(fetchTestByIdforall(testId));
        }
    }, [dispatch, testId, test]);

    const slug = test && test.testName ? slugify(test.testName).substring(0, 60) : '';

    const handleStartTest = () => {
        if (isAuthenticated) {
            navigate(`/exam-terms-conditions`, { state: { test, studentProfileData } });
        } else {
            navigate('/signin');
        }
    };

    const copyShareLink = async () => {
        try {
            setIsCopying(true);
            const shareLink = `${window.location.origin}/test/${slug}/${testId}`;
            await navigator.clipboard.writeText(shareLink);
            setShowCopiedToast(true);
            setTimeout(() => setShowCopiedToast(false), 3000);
        } catch (err) {
            console.error('Failed to copy:', err);
        } finally {
            setIsCopying(false);
        }
    };

    const shareOnWhatsApp = () => {
        const shareText = `Check out this test: ${test.testName}\n\n${window.location.origin}/test/${slug}/${testId}`;
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
    };

    const shareOnTelegram = () => {
        const shareText = `Check out this test: ${test.testName}\n\n${window.location.origin}/test/${slug}/${testId}`;
        window.open(`https://t.me/share/url?url=${encodeURIComponent(shareText)}`, '_blank');
    };

    const shareViaEmail = () => {
        const subject = `Check out this test: ${test.testName}`;
        const body = `I thought you might be interested in this test:\n\n${window.location.origin}/test/${slug}/${testId}`;
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    };

    const calculateStatistics = () => {
        const totalSubmissions = test.submitedBy?.length || 0;
        const passedCount = test.submitedBy?.filter(s => s.passStatus === 'Pass').length || 0;
        const passRate = totalSubmissions > 0 ? Math.round((passedCount / totalSubmissions) * 100) : 0;

        return { totalSubmissions, passedCount, passRate };
    };

    const stats = test ? calculateStatistics() : null;

    if (loading || !test) return (
        <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
            <Spinner animation="border" style={{ color: '#0096c7' }} />
        </Container>
    );

    if (test.error) return (
        <Container className="py-5">
            <Row className="justify-content-center">
                <Col md={8}>
                    <Alert variant="warning" className="text-center">
                        <FaExclamationTriangle className="mb-2" size={24} />
                        <Alert.Heading>Test Not Found</Alert.Heading>
                        <p>The test you're looking for may have been removed, is no longer available, or doesn't exist.</p>
                        <div className="d-flex justify-content-center gap-2">
                            <Button variant="outline-secondary" onClick={() => navigate('/')}>
                                Back to Home
                            </Button>
                            <Button variant="outline-primary" onClick={() => navigate('/alltest')}>
                                Browse Tests
                            </Button>
                        </div>
                    </Alert>
                </Col>
            </Row>
        </Container>
    );

    // Generate SEO data based on test details
    const generateSeoData = () => {
        if (!test) return null;

        const slug = test.testName ? slugify(test.testName).substring(0, 60) : '';
        const title = `${test.testName} - Practice Test | My Exam World`;
        const description = `Take this ${test.testName} practice test with ${test.questions?.length || 0} questions, ${test.totalMarks} marks, and ${test.totalMinutes} minutes time limit. Prepare for your ${test.category} exams with this comprehensive test.`;

        // Extract keywords from test properties
        const keywords = [
            test.testName,
            test.category,
            test.testSeries,
            `${test.testName} practice test`,
            `${test.category} exam`,
            `${test.testSeries} test series`,
            'online test',
            'practice exam',
            'mock test',
            'exam preparation'
        ].filter(Boolean).join(', ');

        return {
            title,
            description: description.substring(0, 160),
            keywords: keywords.substring(0, 255),
            url: `/test/${slug}/${testId}`,
            type: 'article',
            publishedTime: test.createdAt,
            author: test.teacherName,
            section: test.category,
            tags: [test.testName, test.category, test.testSeries, 'practice test', 'online exam']
        };
    };

    const seoData = test ? generateSeoData() : null;

    return (
        <Container className="py-4">
            {/* SEO Component */}
            {seoData && (
                <SEO
                    title={seoData.title}
                    description={seoData.description}
                    keywords={seoData.keywords}
                    url={seoData.url}
                    type={seoData.type}
                    publishedTime={seoData.publishedTime}
                    author={seoData.author}
                    section={seoData.section}
                    tags={seoData.tags}
                    schema={{
                        "@context": "https://schema.org",
                        "@type": "Quiz",
                        "name": test.testName,
                        "description": test.description || seoData.description,
                        "educationalAlignment": {
                            "@type": "AlignmentObject",
                            "educationalFramework": test.category
                        },
                        "hasPart": {
                            "@type": "Question",
                            "name": `${test.totalQuestions || 0} Questions Test`
                        }
                    }}
                />
            )}
            {/* Back button */}
            <Button
                variant="light"
                onClick={() => navigate(-1)}
                className="mb-2 d-flex align-items-center"
                style={{ padding: '0rem 0.5rem', margin: "0" }}
            >
                <FaArrowLeft className="me-2" /> Back
            </Button>

            {/* Toast notification */}
            <Toast
                show={showCopiedToast}
                onClose={() => setShowCopiedToast(false)}
                delay={3000}
                autohide
                className="position-fixed bottom-0 end-0 m-3"
            >
                <Toast.Header closeButton={false}>
                    <strong className="me-auto">Copied!</strong>
                </Toast.Header>
                <Toast.Body>Link copied to clipboard</Toast.Body>
            </Toast>

            {/* Test Header Section */}
            <Row className="mb-4">
                <Col md={8}>
                    <h1 className="mb-3">{test.testName}</h1>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        <Badge bg="light" text="dark" className="d-flex align-items-center">
                            <FaBook className="me-1" /> {test.category}
                        </Badge>
                        <Badge bg="light" text="dark" className="d-flex align-items-center">
                            <FaUserTie className="me-1" /> {test.teacherName}
                        </Badge>
                        <Badge bg="light" text="dark" className="d-flex align-items-center">
                            <FaClock className="me-1" /> {test.totalMinutes} mins
                        </Badge>
                        <Badge bg="light" text="dark" className="d-flex align-items-center">
                            <FaCalendarAlt className="me-1" />
                            {test.createdAt
                                ? (() => {
                                    const d = new Date(test.createdAt);
                                    const day = String(d.getDate()).padStart(2, '0');
                                    const month = String(d.getMonth() + 1).padStart(2, '0');
                                    const year = d.getFullYear();
                                    return `${day}/${month}/${year}`;
                                })()
                                : 'N/A'}
                        </Badge>

                    </div>
                </Col>
                <Col md={4}>
                    <div className="d-flex flex-column flex-md-row gap-2 justify-content-md-end mb-3">
                        <Button
                            // variant="primary"
                            style={{ border: 'none' }}
                            size="lg"
                            onClick={handleStartTest}
                            className="mb-2 mb-md-0 button-design"
                        >
                            {isAuthenticated ? 'Start Test' : 'Login to Attempt'}
                        </Button>

                        <Dropdown as={ButtonGroup} >
                            <Button
                                variant="outline-primary"
                                size="lg"
                                onClick={copyShareLink}
                            >
                                <FaShareAlt className="me-1" /> Share
                            </Button>

                            <Dropdown.Toggle
                                split
                                variant="outline-primary"
                                size="lg"
                            />

                            <Dropdown.Menu align="end">
                                <Dropdown.Header>Share via</Dropdown.Header>
                                <Dropdown.Item onClick={shareOnWhatsApp}>
                                    <FaWhatsapp className="me-2 text-success" /> WhatsApp
                                </Dropdown.Item>
                                <Dropdown.Item onClick={shareOnTelegram}>
                                    <FaTelegram className="me-2 text-primary" /> Telegram
                                </Dropdown.Item>
                                <Dropdown.Item onClick={shareViaEmail}>
                                    <FaEnvelope className="me-2 text-secondary" /> Email
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={copyShareLink}>
                                    {isCopying ? (
                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                    ) : showCopiedToast ? (
                                        <MdOutlineCheck className="me-2 text-success" />
                                    ) : (
                                        <MdOutlineContentCopy className="me-2" />
                                    )}
                                    Copy link
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </Col>
            </Row>

            {/* Main Content Grid */}
            <Row>
                {/* Test Information Card */}

                <Col lg={4} className="mb-4">
                    <Card>
                        <Card.Header>
                            <Card.Title>Test Details</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <ListGroup variant="flush">
                                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                    <span>Test Series</span>
                                    <span className="fw-semibold">{test.testSeries}</span>
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                    <span>Total Questions</span>
                                    <span className="fw-semibold">{test.questions?.length || 0}</span>
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                    <span>Total Marks</span>
                                    <span className="fw-semibold">{test.totalMarks}</span>
                                </ListGroup.Item>
                                <ListGroup.Item className="d-flex justify-content-between align-items-center">
                                    <span>Passing Percentage</span>
                                    <span className="fw-semibold">{test.passingPercentage}%</span>
                                </ListGroup.Item>
                                {test.description && (
                                    <ListGroup.Item>
                                        <div className="d-flex flex-column">
                                            <span className="text-muted small">Description</span>
                                            <span className="mt-1">{test.description}</span>
                                        </div>
                                    </ListGroup.Item>
                                )}
                            </ListGroup>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Statistics Card */}
                <Col lg={4} className="mb-4">
                    <Card>
                        <Card.Header>
                            <Card.Title className="d-flex align-items-center">
                                <FaChartBar className="me-2" />Statistics
                            </Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="">
                                <h5 className="text-muted small">Total Attempts</h5>
                                <p className="fs-3 fw-bold">{stats.totalSubmissions}</p>
                            </div>
                            <div>
                                <h5 className="text-muted small">Pass Rate</h5>
                                <ProgressBar
                                    now={stats.passRate}
                                    label={`${stats.passRate}%`}
                                    variant="success"
                                    className="mb-2"
                                    style={{ height: '28px' }}
                                />
                                <small className="text-muted">
                                    {stats.passedCount} out of {stats.totalSubmissions} passed
                                </small>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Share Card */}
                <Col lg={4} className="mb-4">
                    <Card>
                        <Card.Header>
                            <Card.Title className="d-flex align-items-center">
                                <FaShareAlt className="me-2" />Share This Test
                            </Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div>
                                <p className="text-muted mb-3">Challenge your friends with this test!</p>

                                <InputGroup className="mb-3">
                                    <InputGroup.Text>
                                        <FaLink />
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        value={`${window.location.origin}/test/${slug}/${testId}`}
                                        readOnly
                                    />
                                    <Button
                                        variant="outline-secondary"
                                        onClick={copyShareLink}
                                    >
                                        {isCopying ? (
                                            <Spinner as="span" animation="border" size="sm" />
                                        ) : showCopiedToast ? (
                                            <MdOutlineCheck />
                                        ) : (
                                            <MdOutlineContentCopy />
                                        )}
                                    </Button>
                                </InputGroup>

                                <div className="d-flex flex-wrap gap-2">
                                    <Button
                                        variant="outline-success"
                                        onClick={shareOnWhatsApp}
                                        className="d-flex align-items-center"
                                    >
                                        <FaWhatsapp className="me-2" /> WhatsApp
                                    </Button>
                                    <Button
                                        variant="outline-primary"
                                        onClick={shareOnTelegram}
                                        className="d-flex align-items-center"
                                    >
                                        <FaTelegram className="me-2" /> Telegram
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        onClick={shareViaEmail}
                                        className="d-flex align-items-center"
                                    >
                                        <FaEnvelope className="me-2" /> Email
                                    </Button>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default TestDetailsPage;